import axiosInstance from './api';

const USERS_URL = '/users/';

let pendingMfaChallengeRequests = {};

const authService = {
  login: async (username, password, code = null, backupCode = null) => {
    try {
      console.log('Auth service login called with:', { 
        username, 
        passwordProvided: !!password, 
        codeProvided: !!code, 
        backupCodeProvided: !!backupCode 
      });

      console.log('Login parameters:', {
        hasUsername: !!username,
        hasPassword: !!password,
        hasCode: !!code,
        hasBackupCode: !!backupCode,
        isCompletingMfa: !!code || !!backupCode
      });

      const payload = { username };
    
      if (password) payload.password = password;
      if (code) payload.code = code;
      if (backupCode) payload.backup_code = backupCode;

      const endpoint = '/users/auth/login/';
      console.log(`Making login request to ${endpoint} with payload:`, payload);

      const response = await axiosInstance.post(endpoint, payload);

      console.log('Login response status:', response.status);
      console.log('Login response detailed:', {
        hasToken: !!response.data.token,
        requiresMfa: !!response.data.requires_mfa || !!response.data.requires_2fa,
        responseStatus: response.data.status,
        hasUserObject: !!response.data.user,
        responseKeys: Object.keys(response.data)
      });

      if (response.data.requires_2fa || response.data.requires_mfa) {
        console.log('MFA is required after login attempt.');
        return {
          requires_2fa: true,
          requires_mfa: true, // Add both for compatibility
          username,
          message: response.data.message || '2FA verification required'
        };
      }

      if (response.data.status === 'success' && response.data.token) {
        console.log('Successful login with token. Storing token and setting headers.');
        localStorage.setItem('token', response.data.token);
        axiosInstance.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;

        if (code || backupCode) {
          sessionStorage.setItem('mfa_verified_session', 'true');
          console.log('Setting MFA verified session flag after successful 2FA login');
        }

        const user = response.data.user || {};
        if (user.mfa_enabled || user.two_factor_enabled) {
          sessionStorage.setItem('mfa_verified_session', 'true');
        }

        return {
          token: response.data.token,
          user,
          roles: response.data.roles || [],
          permissions: response.data.permissions || []
        };
      }

      if (response.data.token) {
        console.log('Login succeeded, token present but no status "success".');
        localStorage.setItem('token', response.data.token);
        axiosInstance.defaults.headers.common['Authorization'] = `Token ${response.data.token}`;

        const user = response.data.user || {};
        if ((code || backupCode) && (user.mfa_enabled || user.two_factor_enabled)) {
          sessionStorage.setItem('mfa_verified_session', 'true');
          console.log('Setting MFA verified session flag after successful 2FA login');
        }

        return {
          token: response.data.token,
          user: response.data.user,
          roles: response.data.roles || [],
          permissions: response.data.permissions || []
        };
      }

      if (response.data.status === 'success') {
        console.warn('Success response without expected token format:', response.data);

        if (response.data.user) {
          const token = response.data.auth_token || `token_${Date.now()}`;
          console.warn('Simulating token generation and storing:', token);
          localStorage.setItem('token', token);
          axiosInstance.defaults.headers.common['Authorization'] = `Token ${token}`;

          const user = response.data.user || {};
          if ((code || backupCode) && (user.mfa_enabled || user.two_factor_enabled)) {
            sessionStorage.setItem('mfa_verified_session', 'true');
          }

          return {
            token,
            user: response.data.user,
            roles: response.data.roles || [],
            permissions: response.data.permissions || []
          };
        }
      }

      console.error('Login response lacks expected format:', response.data);
      throw new Error('Login failed: Invalid response format');
    } catch (error) {
      console.error('Login error caught:', error);
      console.error('Error response data:', error.response?.data);

      if (error.response?.status === 400) {
        if (error.response?.data?.error === "Invalid 2FA code") {
          throw new Error('Invalid verification code. Please try again.');
        }
        if (error.response?.data?.password) {
          throw new Error(`Password error: ${error.response.data.password}`);
        }
        if (error.response?.data?.errors) {
          throw new Error(JSON.stringify(error.response.data.errors));
        }
      }

      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Login failed'
      );
    }
  },

  setupMFA: async (password, operationType = 'FUND_MANAGEMENT') => {
    try {
      console.log(`Auth service setupMFA called with operation type: ${operationType}`);

      const payload = {
        password,
        operation_type: operationType.toLowerCase() // Normalize to lowercase for API
      };

      console.log('Making setupMFA request with payload:', {
        hasPassword: !!password,
        operationType: operationType.toLowerCase()
      });

      const response = await axiosInstance.post('/users/mfa/setup/', payload);

      console.log('SetupMFA response status:', response.status);
      console.log('SetupMFA response data keys:', Object.keys(response.data));

      return response.data;
    } catch (error) {
      console.error('SetupMFA error caught:', error);
      console.error('Error response data:', error.response?.data);

      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.detail ||
        error.message ||
        'Failed to set up MFA'
      );
    }
  },

  setup2FA: async (password) => {
    try {
      const response = await axiosInstance.post('/users/users/enable_2fa/', { password });
      return response.data;
    } catch (error) {
      console.error('2FA setup error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to setup 2FA');
    }
  },

  verify2FA: async (code) => {
    try {
      const response = await axiosInstance.post('/users/users/verify_2fa/', { code });

      sessionStorage.setItem('mfa_verified_session', 'true');

      return response.data;
    } catch (error) {
      console.error('2FA verification error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to verify 2FA');
    }
  },

  verifyMfaSession: async (code, operationType = 'FUND_MANAGEMENT', useBackupCode = false) => {
    try {
      const response = await axiosInstance.post('/users/mfa/verify-session/', {
        code,
        operation_type: operationType.toLowerCase(), // Normalize to lowercase for API
        is_backup_code: useBackupCode,
      });

      sessionStorage.setItem('mfa_verified_session', 'true');
      return response.data;
    } catch (error) {
      console.error('MFA session verification failed', error);
      throw error;
    }
  },

  disable2FA: async (password) => {
    try {
      const response = await axiosInstance.post('/users/users/disable_2fa/', { password });
      return response.data;
    } catch (error) {
      console.error('2FA disable error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to disable 2FA');
    }
  },

  generateMfaChallenge: async (operationType = 'FUND_MANAGEMENT', verificationMethod = 'totp', userId = null) => {
    const requestKey = `${operationType}_${verificationMethod}_${userId || 'none'}`;

    if (pendingMfaChallengeRequests[requestKey]) {
      console.log(`Reusing pending MFA challenge request for ${requestKey}`);
      return pendingMfaChallengeRequests[requestKey];
    }

    try {
      const payload = {
        operation_type: operationType.toLowerCase(), // Normalize to lowercase for API
        verification_method: verificationMethod,
        context: {
          target_model: 'user',
          action: operationType.toLowerCase(),
          subject: 'authentication',
        },
      };

      if (userId) {
        payload.user_id = userId;
      }

      console.log('Sending MFA challenge request with payload:', payload);

      const requestPromise = (async () => {
        try {
          const response = await axiosInstance.post('/users/mfa/challenge/', payload);
          return response.data;
        } finally {
          setTimeout(() => {
            delete pendingMfaChallengeRequests[requestKey];
          }, 2000);
        }
      })();

      pendingMfaChallengeRequests[requestKey] = requestPromise;

      return await requestPromise;
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to generate MFA challenge';
      console.error('Generate MFA challenge error:', errorMessage);

      delete pendingMfaChallengeRequests[requestKey];

      throw new Error(errorMessage);
    }
  },

  regenerateBackupCodes: async (password) => {
    try {
      const response = await axiosInstance.post('/users/users/regenerate_backup_codes/', { password });
      return response.data;
    } catch (error) {
      console.error('Regenerate backup codes error:', error);
      throw new Error(error.response?.data?.detail || 'Failed to regenerate backup codes');
    }
  },

  // Add the missing getUserContext method that AuthContext expects
  getUserContext: async () => {
    try {
      console.log('Fetching user context (branches/departments)');
      const response = await axiosInstance.get('/users/auth/user_context/');
      
      console.log('User context response:', {
        branchId: response.data.branch_id,
        departmentId: response.data.department_id,
        isCrossBranch: response.data.is_cross_branch,
        isCrossDepartment: response.data.is_cross_department,
        accessibleBranches: response.data.accessible_branches_data?.length || 0,
        accessibleDepartments: response.data.accessible_departments_data?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching user context:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch user context'
      );
    }
  },

  getUserBranches: async (userId) => {
    try {
      console.log(`Fetching branches for user ${userId}`);
      const response = await axiosInstance.get(`/users/users/${userId}/branches/`);

      console.log('User branches response:', {
        branchesCount: response.data.branches?.length || 0,
        userBranchesCount: response.data.user_branches?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching user branches:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch user branches'
      );
    }
  },

  getUserDepartments: async (userId) => {
    try {
      console.log(`Fetching departments for user ${userId}`);
      const response = await axiosInstance.get(`/users/users/${userId}/departments/`);

      console.log('User departments response:', {
        departmentsCount: response.data.departments?.length || 0,
        userDepartmentsCount: response.data.user_departments?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching user departments:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch user departments'
      );
    }
  },

  getBranchDepartments: async (branchId) => {
    try {
      console.log(`Fetching departments for branch ${branchId}`);
      const response = await axiosInstance.get(`/core/branches/${branchId}/departments/`);
      console.log('Branch departments response:', {
        departmentsCount: response.data.departments?.length || 0,
        fullResponse: response.data
      });
    
      // Ensure we always return a consistent structure
      return {
        departments: response.data.departments || response.data.results || response.data || [],
        count: response.data.count || response.data.departments?.length || 0,
        ...response.data // preserve any other fields
      };
    } catch (error) {
      console.error('Error fetching branch departments:', error);
    
      // If it's a 404 or the branch simply has no departments, return empty array
      if (error.response?.status === 404 || 
          error.response?.status === 204 ||
          error.response?.data?.departments === null ||
          (error.response?.data?.detail && error.response.data.detail.includes('no departments'))) {
      
        console.log(`Branch ${branchId} has no departments, returning empty array`);
        return {
          departments: [],
          count: 0
        };
      }
    
      // For other errors, still throw
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch branch departments'
      );
    }
  },

  getAllBranches: async () => {
    try {
      console.log('Fetching all branches');
      const response = await axiosInstance.get('/core/branches/');

      console.log('All branches response:', {
        branchesCount: response.data.length || response.data.results?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching all branches:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch branches'
      );
    }
  },

  getAllDepartments: async () => {
    try {
      console.log('Fetching all departments');
      const response = await axiosInstance.get('/core/departments/');

      console.log('All departments response:', {
        departmentsCount: response.data.length || response.data.results?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching all departments:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch departments'
      );
    }
  },

  updateUserBranch: async (userId, branchId, isCrossBranch = false) => {
    try {
      console.log(`Updating user ${userId} branch assignment to ${branchId}`);
      const response = await axiosInstance.patch(`/users/users/${userId}/`, {
        branch_id: branchId,
        is_cross_branch: isCrossBranch
      });

      console.log('User branch update successful');
      return response.data;
    } catch (error) {
      console.error('Error updating user branch:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to update user branch'
      );
    }
  },

  updateUserDepartment: async (userId, departmentId, isCrossDepartment = false) => {
    try {
      console.log(`Updating user ${userId} department assignment to ${departmentId}`);
      const response = await axiosInstance.patch(`/users/users/${userId}/`, {
        department_id: departmentId,
        is_cross_department: isCrossDepartment
      });

      console.log('User department update successful');
      return response.data;
    } catch (error) {
      console.error('Error updating user department:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to update user department'
      );
    }
  },

  getUserContextPermissions: async (userId, branchId = null, departmentId = null) => {
    try {
      const params = new URLSearchParams();
      if (branchId) params.append('branch_id', branchId);
      if (departmentId) params.append('department_id', departmentId);

      const url = `/users/users/${userId}/permissions/?${params.toString()}`;
      console.log(`Fetching context permissions for user ${userId}:`, { branchId, departmentId });

      const response = await axiosInstance.get(url);

      console.log('Context permissions response:', {
        permissionsCount: response.data.permissions?.length || 0,
        rolesCount: response.data.roles?.length || 0
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching context permissions:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch context permissions'
      );
    }
  },

  switchUserBranch: async (branchId) => {
    try {
      console.log(`Switching to branch ${branchId}`);
      const response = await axiosInstance.post('/users/switch-branch/', {
        branch_id: branchId
      });

      console.log('Branch switch successful');
      return response.data;
    } catch (error) {
      console.error('Error switching branch:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to switch branch'
      );
    }
  },

  switchUserDepartment: async (departmentId) => {
    try {
      console.log(`Switching to department ${departmentId}`);
      const response = await axiosInstance.post('/users/switch-department/', {
        department_id: departmentId
      });

      console.log('Department switch successful');
      return response.data;
    } catch (error) {
      console.error('Error switching department:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to switch department'
      );
    }
  },

  getUserFundLimits: async (userId, branchId = null, departmentId = null) => {
    try {
      const params = new URLSearchParams();
      if (branchId) params.append('branch_id', branchId);
      if (departmentId) params.append('department_id', departmentId);

      const url = `/users/users/${userId}/fund-limits/?${params.toString()}`;
      console.log(`Fetching fund limits for user ${userId}:`, { branchId, departmentId });

      const response = await axiosInstance.get(url);

      console.log('Fund limits response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching fund limits:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to fetch fund limits'
      );
    }
  },

  logout: async () => {
    try {
      await axiosInstance.post('/users/auth/logout/');
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      localStorage.removeItem('token');
      sessionStorage.removeItem('mfa_verified_session');
      sessionStorage.removeItem('mfa_verified_operations');
      sessionStorage.removeItem('current_branch_id');
      sessionStorage.removeItem('current_department_id');
      delete axiosInstance.defaults.headers.common['Authorization'];

      pendingMfaChallengeRequests = {};
    }
  },

  register: async (username, email, password, firstName, lastName, phoneNumber) => {
    try {
      const response = await axiosInstance.post('/users/users/register/', {
        username,
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      if (error.response) {
        console.error('Server response:', error.response.data);
        throw new Error(error.response.data.detail || JSON.stringify(error.response.data));
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error('Error setting up request');
      }
    }
  },

  getAllUsers: async () => {
    try {
      const response = await axiosInstance.get(`${USERS_URL}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch users');
    }
  },

  getUser: async (id) => {
    try {
      const response = await axiosInstance.get(`${USERS_URL}${id}/`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new Error('Not authenticated');
      }
      throw new Error(error.response?.data?.detail || 'Failed to fetch user data');
    }
  },

  getCurrentUser: async () => {
    console.log('getCurrentUser called');
    try {
      const response = await axiosInstance.get('/users/me/');
      console.log('getCurrentUser response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getCurrentUser error:', error);
      throw error;
    }
  }
};

export default authService;
